/**
 * ExportDialog Component
 * 
 * Dialog para configurar e executar exportação de lançamentos
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Checkbox } from '../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Download, FileJson, Calendar, Filter, FileSpreadsheet } from 'lucide-react';
import { useExportTransactions } from '../hooks/useExportTransactions';
import { exportTransactionsToExcel } from '../services/export-excel.service';
import { supabase } from '../../../lib/supabase';
import type { ExportFilters } from '../../../types/export-import';
import { toast } from 'sonner';

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
  companyName: string;
  transactionCount?: number;
}

export function ExportDialog({
  open,
  onOpenChange,
  companyId,
  companyName,
  transactionCount = 0,
}: ExportDialogProps) {
  const { exportTransactions, isExporting } = useExportTransactions({
    companyId,
    companyName,
  });

  // Defaults: último mês
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const defaultStartDate = firstDayOfMonth.toISOString().split('T')[0];
  const defaultEndDate = today.toISOString().split('T')[0];

  const [filters, setFilters] = useState<ExportFilters>({
    start_date: defaultStartDate,
    end_date: defaultEndDate,
    entry_type: 'all',
    payment_status: 'all',
    include_recurring: true,
    include_simulations: false,
  });

  const [format, setFormat] = useState<'json' | 'excel'>('excel');
  const [includeMasterData, setIncludeMasterData] = useState(true);

  const handleExport = async () => {
    try {
      if (format === 'excel') {
        // Export to Excel - fetch ALL transactions using pagination
        let allTransactions: any[] = [];
        let hasMore = true;
        let page = 0;
        const pageSize = 1000; // Supabase max per request

        console.log('[Export] Fetching all transactions for export...');

        // Fetch all pages
        while (hasMore) {
          const from = page * pageSize;
          const to = from + pageSize - 1;

        const { data: transactions, error } = await supabase
          .from('cash_flow_entries')
          .select('*')
          .eq('company_id', companyId)
          .gte('entry_date', filters.start_date)
          .lte('entry_date', filters.end_date)
            .order('entry_date', { ascending: false })
            .range(from, to);

        if (error) throw error;

          if (transactions && transactions.length > 0) {
            allTransactions = [...allTransactions, ...transactions];
            console.log(`[Export] Page ${page + 1}: ${transactions.length} records (total: ${allTransactions.length})`);
            
            // Check if there are more records
            hasMore = transactions.length === pageSize;
            page++;
          } else {
            hasMore = false;
          }
        }

        console.log(`[Export] Total fetched: ${allTransactions.length} transactions`);

        // Apply filters
        let filteredTransactions = allTransactions;
        
        if (filters.entry_type !== 'all') {
          filteredTransactions = filteredTransactions.filter(t => t.entry_type === filters.entry_type);
        }
        
        if (filters.payment_status !== 'all') {
          filteredTransactions = filteredTransactions.filter(t => t.payment_status === filters.payment_status);
        }
        
        if (!filters.include_recurring) {
          filteredTransactions = filteredTransactions.filter(t => !t.is_recurring);
        }
        
        if (!filters.include_simulations) {
          filteredTransactions = filteredTransactions.filter(t => !t.is_simulation);
        }

        console.log(`[Export] After filters: ${filteredTransactions.length} transactions`);

        await exportTransactionsToExcel(filteredTransactions, companyId, {
          includeMasterData,
        });

        toast.success(`${filteredTransactions.length} lançamentos exportados para Excel!`);
      } else {
        // Export to JSON (existing)
        exportTransactions(filters);
      }

      // Fechar dialog após 1 segundo
      setTimeout(() => {
        onOpenChange(false);
      }, 1000);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar lançamentos');
    }
  };

  const handleQuickPeriod = async (period: 'current_month' | 'last_month' | 'current_year' | 'all') => {
    const today = new Date();
    let start_date: string;
    let end_date: string;

    switch (period) {
      case 'current_month':
        start_date = new Date(today.getFullYear(), today.getMonth(), 1)
          .toISOString()
          .split('T')[0];
        end_date = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        start_date = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          .toISOString()
          .split('T')[0];
        end_date = new Date(today.getFullYear(), today.getMonth(), 0)
          .toISOString()
          .split('T')[0];
        break;
      case 'current_year':
        start_date = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end_date = today.toISOString().split('T')[0];
        break;
      case 'all':
        start_date = '2020-01-01';
        
        // Buscar a data do último lançamento (mais futuro)
        try {
          const { data: lastTransaction } = await supabase
            .from('cash_flow_entries')
            .select('entry_date')
            .eq('company_id', companyId)
            .order('entry_date', { ascending: false })
            .limit(1)
            .single();

          if (lastTransaction && lastTransaction.entry_date) {
            end_date = lastTransaction.entry_date;
            console.log(`[Export] Último lançamento encontrado: ${end_date}`);
          } else {
            // Fallback para hoje se não houver lançamentos
            end_date = today.toISOString().split('T')[0];
          }
        } catch (error) {
          console.error('[Export] Erro ao buscar último lançamento:', error);
          // Fallback para hoje em caso de erro
        end_date = today.toISOString().split('T')[0];
        }
        break;
    }

    setFilters((prev) => ({ ...prev, start_date, end_date }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Exportar Lançamentos
          </DialogTitle>
          <DialogDescription>
            Configure o período e filtros para exportar seus lançamentos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Formato de Export */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <FileJson className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-semibold">Formato</Label>
            </div>

            <RadioGroup value={format} onValueChange={(value: 'json' | 'excel') => setFormat(value)}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="excel" id="excel" />
                <label
                  htmlFor="excel"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-green-600" />
                    <div>
                      <p className="font-medium">Excel (.xlsx)</p>
                      <p className="text-xs text-muted-foreground">
                        Visual e editável - Ideal para edição e compartilhamento
                      </p>
                    </div>
                  </div>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="json" id="json" />
                <label
                  htmlFor="json"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <FileJson className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="font-medium">JSON (.json)</p>
                      <p className="text-xs text-muted-foreground">
                        Técnico - Ideal para backup e integrações
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </RadioGroup>

            {format === 'excel' && (
              <div className="flex items-center space-x-2 pl-6 pt-2">
                <Checkbox
                  id="include_master_data"
                  checked={includeMasterData}
                  onCheckedChange={(checked) => setIncludeMasterData(checked === true)}
                />
                <label
                  htmlFor="include_master_data"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Incluir dados mestres (Categorias, Métodos, Parceiros)
                </label>
              </div>
            )}
          </div>

          {/* Período */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-semibold">Período</Label>
            </div>

            {/* Atalhos rápidos */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickPeriod('current_month')}
              >
                Mês Atual
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickPeriod('last_month')}
              >
                Mês Passado
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickPeriod('current_year')}
              >
                Ano Atual
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleQuickPeriod('all')}
              >
                Todos
              </Button>
            </div>

            {/* Datas personalizadas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Data Início</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, start_date: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Data Fim</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, end_date: e.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Label className="text-base font-semibold">Filtros</Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="entry_type">Tipo de Lançamento</Label>
                <Select
                  value={filters.entry_type}
                  onValueChange={(value: any) =>
                    setFilters((prev) => ({ ...prev, entry_type: value }))
                  }
                >
                  <SelectTrigger id="entry_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="income">Receitas</SelectItem>
                    <SelectItem value="expense">Despesas</SelectItem>
                    <SelectItem value="transfer">Transferências</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_status">Status de Pagamento</Label>
                <Select
                  value={filters.payment_status}
                  onValueChange={(value: any) =>
                    setFilters((prev) => ({ ...prev, payment_status: value }))
                  }
                >
                  <SelectTrigger id="payment_status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="confirmado">Confirmados</SelectItem>
                    <SelectItem value="previsão">Previsões</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Opções adicionais */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_recurring"
                  checked={filters.include_recurring}
                  onCheckedChange={(checked) =>
                    setFilters((prev) => ({ ...prev, include_recurring: checked === true }))
                  }
                />
                <label
                  htmlFor="include_recurring"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Incluir lançamentos recorrentes
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="include_simulations"
                  checked={filters.include_simulations}
                  onCheckedChange={(checked) =>
                    setFilters((prev) => ({ ...prev, include_simulations: checked === true }))
                  }
                />
                <label
                  htmlFor="include_simulations"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Incluir simulações
                </label>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-lg border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <FileJson className="h-5 w-5 text-primary mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium">Formato de Exportação</p>
                <p className="text-xs text-muted-foreground">
                  Arquivo JSON estruturado contendo lançamentos e dados relacionados (categorias,
                  métodos de pagamento, parceiros, etc.)
                </p>
                {transactionCount > 0 && (
                  <p className="text-xs text-primary font-medium pt-1">
                    INFO: Total disponível: {transactionCount} lançamentos na base
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isExporting}>
            Cancelar
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            {isExporting ? (
              <>
                <span className="animate-spin mr-2">⏳</span>
                Exportando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

